import { RISK_OVERVIEW_FACTOR_GROUP_ORDER } from './riskOverviewWorkbook';
import {
    isAssignedAttributeGroup,
    isWatchlistGroupingAttribute,
} from './watchlistAttributePresentation';

const SUMMARY_METRICS = Object.freeze([
    { id: 'position', label: 'Position', key: 'positionCount', type: 'number' },
    { id: 'nmv', label: 'NMV', key: 'nmv', type: 'currency' },
    { id: 'gmv', label: 'GMV', key: 'gmv', type: 'currency' },
    { id: 'betaExp', label: 'Beta Exposure', key: 'betaExp', type: 'currency' },
    { id: 'effectiveBreadth', label: 'Effective Breadth', key: 'effectiveBreadth', type: 'round' },
    { id: 'totalRiskDollar', label: 'Total Risk ($)', key: 'totalRiskDollar', type: 'currency' },
    { id: 'totalRiskBps', label: 'Total Risk (bps)', key: 'totalRiskBps', type: 'round' },
    { id: 'factorRiskBps', label: 'Factor Risk (bps)', key: 'factorRiskBps', type: 'round' },
    { id: 'idioRiskBps', label: 'Idio Risk (bps)', key: 'idioRiskBps', type: 'round' },
    { id: 'factorRiskPct', label: 'Factor Risk (%)', key: 'factorRiskPct', type: 'percentage' },
    { id: 'idioRiskPct', label: 'Idio Risk (%)', key: 'idioRiskPct', type: 'percentage' },
]);

export const GROUP_METRICS = Object.freeze([
    { metricKey: 'nmv', label: 'NMV', absKey: 'nmv', pctUcapKey: 'nmv_pct_ucap', pctAumKey: 'nmv_pct_aum' },
    { metricKey: 'beta_exp', label: 'Beta Exposure', absKey: 'beta_exp', pctUcapKey: 'beta_exp_pct_ucap', pctAumKey: 'beta_exp_pct_aum' },
    { metricKey: 'factor_vol', label: 'Factor Vol.', absKey: 'factor_vol', pctUcapKey: 'factor_vol_pct_ucap', pctAumKey: 'factor_vol_pct_aum' },
    { metricKey: 'idio_vol', label: 'Idio Vol.', absKey: 'idio_vol', pctUcapKey: 'idio_vol_pct_ucap', pctAumKey: 'idio_vol_pct_aum' },
    { metricKey: 'total_vol', label: 'Total Vol.', absKey: 'total_vol', pctUcapKey: 'total_vol_pct_ucap', pctAumKey: 'total_vol_pct_aum' },
    { metricKey: 'factor_pct', label: 'Factor % of Total Risk', absKey: 'factor_pct' },
    { metricKey: 'idio_pct', label: 'Idio % of Total Risk', absKey: 'idio_pct' },
    { metricKey: 'gmv', label: 'GMV', absKey: 'gmv' },
    { metricKey: 'ucap', label: 'UCap', absKey: 'ucap' },
    { metricKey: 'aum', label: 'AUM', absKey: 'aum' },
]);

const PLACEHOLDER_BUCKETS = Object.freeze([
    { groupLabel: 'Style', count: 6 },
    { groupLabel: 'Industry', count: 8 },
    { groupLabel: 'Country', count: 4 },
    { groupLabel: 'Currency', count: 4 },
    { groupLabel: 'Market / Regional', count: 3 },
    { groupLabel: 'Other / Unknown', count: 2 },
]);
const EMPTY_RISK_FACTOR_ROW_COUNT = 12;
const RESERVED_SUMMARY_HEADERS = new Set(['actual portfolio', 'summary']);

export function toNullableNumber(value) {
    if (value === null || value === undefined || value === '') return null;
    const number = Number(typeof value === 'string' ? value.replace('%', '') : value);
    return Number.isFinite(number) ? number : null;
}

export function formatPercentPoints(value) {
    const number = toNullableNumber(value);
    return number == null ? '-' : `${number.toFixed(2)}%`;
}

export function formatInteger(value) {
    const number = toNullableNumber(value);
    return number == null ? '-' : Math.round(number).toLocaleString();
}

function normalizeSelectedPortfolioLabel(value) {
    const normalized = String(value || '').trim();
    return !normalized || RESERVED_SUMMARY_HEADERS.has(normalized.toLowerCase())
        ? 'Selected Portfolio'
        : normalized;
}

const requireSelectedExportDecision = (value) => {
    if (typeof value !== 'boolean') {
        throw new TypeError('Selected export inclusion must be explicitly enabled or disabled.');
    }
};

const formatSummaryExportValue = (value, type) => {
    const number = toNullableNumber(value);
    if (number == null) return '-';
    if (type === 'percentage') return `${number.toFixed(2)}%`;
    if (type === 'currency') {
        return number.toLocaleString(undefined, { maximumFractionDigits: 0 });
    }
    return Math.round(number);
};

export function buildSummaryPresentation({
    calculations = {},
    selectedCalculations = {},
    selectedPortfolioLabel = 'Selected Portfolio',
    includeSelectedExport,
} = {}) {
    requireSelectedExportDecision(includeSelectedExport);
    const selectedLabel = normalizeSelectedPortfolioLabel(selectedPortfolioLabel);
    const rows = SUMMARY_METRICS.map((metric) => Object.freeze({
        id: metric.id,
        label: metric.label,
        actual: toNullableNumber(calculations?.[metric.key]),
        selected: toNullableNumber(selectedCalculations?.[metric.key]),
        type: metric.type,
    }));
    const exportRows = rows.map((row) => Object.freeze({
        Summary: row.label,
        'Actual Portfolio': formatSummaryExportValue(row.actual, row.type),
        ...(includeSelectedExport ? {
            [selectedLabel]: formatSummaryExportValue(row.selected, row.type),
        } : {}),
    }));
    return Object.freeze({
        selectedLabel,
        rows: Object.freeze(rows),
        exportRows: Object.freeze(exportRows),
    });
}

const createFactorGroups = () => Object.fromEntries(
    RISK_OVERVIEW_FACTOR_GROUP_ORDER.map((group) => [group, []]),
);

function groupFactors(factorRows) {
    const groups = createFactorGroups();
    (Array.isArray(factorRows) ? factorRows : []).forEach((factor) => {
        const group = RISK_OVERVIEW_FACTOR_GROUP_ORDER.includes(factor?.factorGroup)
            ? factor.factorGroup
            : 'Other / Unknown';
        groups[group].push(factor);
    });
    return groups;
}

const indexFactors = (factors) => new Map(
    (Array.isArray(factors) ? factors : [])
        .filter((factor) => String(factor?.factor || '').trim())
        .map((factor) => [String(factor.factor).trim(), factor]),
);

const zeroFactorFromCatalog = (factor) => ({
    ...factor,
    factor: String(factor?.factor || '').trim(),
    percentVar: 0,
    exposure: 0,
    euler_risk_contrib: 0,
    factor_return_zscore: toNullableNumber(factor?.factor_return_zscore),
});

const materializeFactorIndex = (summaries, catalog) => {
    const summaryByName = indexFactors(summaries);
    const materialized = new Map();
    indexFactors(catalog).forEach((factor, factorName) => {
        materialized.set(
            factorName,
            summaryByName.get(factorName) ?? zeroFactorFromCatalog(factor),
        );
    });
    summaryByName.forEach((factor, factorName) => {
        materialized.set(factorName, factor);
    });
    return materialized;
};

const factorValue = (factor, field) => {
    if (!factor) return null;
    if (field === 'label') return String(factor.factor || '');
    if (field === 'percentVar') return toNullableNumber(factor.percentVar);
    if (field === 'exposure') return toNullableNumber(factor.exposure);
    if (field === 'euler') return toNullableNumber(factor.euler_risk_contrib);
    if (field === 'zscore') return toNullableNumber(factor.factor_return_zscore);
    return null;
};

const compareNullable = (left, right, direction) => {
    if (left == null && right == null) return 0;
    if (left == null) return 1;
    if (right == null) return -1;
    if (typeof left === 'string' || typeof right === 'string') {
        return direction * String(left).localeCompare(String(right));
    }
    return direction * (left - right);
};

const sumFactorValues = (factors, field) => {
    const values = (Array.isArray(factors) ? factors : [])
        .map((factor) => factorValue(factor, field))
        .filter((value) => value != null);
    return values.length === 0
        ? null
        : values.reduce((sum, value) => sum + value, 0);
};

export function buildRiskFactorPresentation({
    calculations = {},
    selectedCalculations = {},
    emptyPortfolio = false,
    sorting = [{ id: 'percentVar', desc: true }],
    includeSelectedExport,
    showAllModelFactors = false,
} = {}) {
    requireSelectedExportDecision(includeSelectedExport);
    const factors = Array.isArray(calculations?.factorExposures)
        ? calculations.factorExposures
        : [];
    const selectedFactors = Array.isArray(selectedCalculations?.factorExposures)
        ? selectedCalculations.factorExposures
        : [];
    const factorCatalog = Array.isArray(calculations?.modelFactorCatalog)
        ? calculations.modelFactorCatalog
        : [];
    const selectedFactorCatalog = Array.isArray(
        selectedCalculations?.modelFactorCatalog,
    )
        ? selectedCalculations.modelFactorCatalog
        : [];
    const actualSummaryByName = indexFactors(factors);
    const selectedSummaryByName = indexFactors(selectedFactors);
    const actualByName = materializeFactorIndex(factors, factorCatalog);
    const selectedByName = materializeFactorIndex(
        selectedFactors,
        selectedFactorCatalog,
    );
    const displayFactorNames = [...new Set([
        ...(showAllModelFactors ? actualByName.keys() : actualSummaryByName.keys()),
        ...(showAllModelFactors ? selectedByName.keys() : selectedSummaryByName.keys()),
    ])];
    const displayFactors = displayFactorNames.map((factorName) => (
        actualByName.get(factorName) ?? selectedByName.get(factorName)
    ));
    const groups = groupFactors(displayFactors);
    const activeSort = sorting?.[0];
    const direction = activeSort?.desc === false ? 1 : -1;
    const selectedField = activeSort?.id?.endsWith('Opt')
        ? activeSort.id.slice(0, -3)
        : null;

    const sortedGroups = createFactorGroups();
    RISK_OVERVIEW_FACTOR_GROUP_ORDER.forEach((groupName) => {
        sortedGroups[groupName] = [...groups[groupName]].sort((left, right) => {
            const leftName = String(left?.factor || '').trim();
            const rightName = String(right?.factor || '').trim();
            const field = selectedField || activeSort?.id;
            const leftSource = selectedField
                ? selectedByName.get(leftName)
                : actualByName.get(leftName);
            const rightSource = selectedField
                ? selectedByName.get(rightName)
                : actualByName.get(rightName);
            const compared = compareNullable(
                factorValue(leftSource, field),
                factorValue(rightSource, field),
                direction,
            );
            return compared || leftName.localeCompare(rightName);
        });
    });

    if (emptyPortfolio) {
        return Object.freeze({
            hasSelectedData: false,
            rows: Object.freeze(Array.from({ length: EMPTY_RISK_FACTOR_ROW_COUNT }, (_, index) => Object.freeze({
                id: `risk-factor-empty-${index}`,
                type: 'factor',
                label: '',
                _stripeIndex: index,
                isPlaceholder: true,
            }))),
            summaryExportRows: Object.freeze([]),
            groupedExportRows: Object.freeze({}),
        });
    }

    const hasSelectedData = (
        selectedFactors.length > 0 || selectedFactorCatalog.length > 0
    );
    const totalExposure = sumFactorValues(factors, 'exposure');
    const selectedTotalExposure = sumFactorValues(selectedFactors, 'exposure');
    const totalEuler = sumFactorValues(factors, 'euler');
    const selectedTotalEuler = sumFactorValues(selectedFactors, 'euler');
    const summaryPercent = toNullableNumber(calculations?.factorRiskPct);
    const selectedSummaryPercent = toNullableNumber(selectedCalculations?.factorRiskPct);
    const summaryExportRows = [Object.freeze({
        Factor: 'Portfolio Total',
        '% Var': formatPercentPoints(summaryPercent),
        ...(includeSelectedExport ? {
            '% Var OPT': hasSelectedData ? formatPercentPoints(selectedSummaryPercent) : '-',
        } : {}),
        Exposure: formatInteger(totalExposure),
        ...(includeSelectedExport ? {
            'Exposure OPT': hasSelectedData ? formatInteger(selectedTotalExposure) : '-',
        } : {}),
        'Factor Risk': formatInteger(totalEuler),
        ...(includeSelectedExport ? {
            'Factor Risk OPT': hasSelectedData ? formatInteger(selectedTotalEuler) : '-',
        } : {}),
        'Rtn. Z-Score': '-',
    })];

    let stripeIndex = 0;
    const rows = [Object.freeze({
        id: 'summary',
        type: 'summary',
        label: 'Portfolio Total',
        percentVar: summaryPercent,
        percentVarOpt: hasSelectedData ? selectedSummaryPercent : null,
        exposure: totalExposure,
        exposureOpt: hasSelectedData ? selectedTotalExposure : null,
        euler: totalEuler,
        eulerOpt: hasSelectedData ? selectedTotalEuler : null,
        zscore: null,
        _stripeIndex: stripeIndex++,
    })];
    const groupedExportRows = {};

    if (displayFactorNames.length === 0) {
        PLACEHOLDER_BUCKETS.forEach(({ groupLabel, count }) => {
            rows.push(Object.freeze({
                id: `bucket-placeholder-${groupLabel}`,
                type: 'bucket',
                bucketLabel: groupLabel,
                label: groupLabel,
                _stripeIndex: null,
            }));
            Array.from({ length: count }).forEach((_, index) => rows.push(Object.freeze({
                id: `placeholder-${groupLabel}-${index}`,
                type: 'factor',
                bucketLabel: groupLabel,
                factor: null,
                label: '',
                percentVar: null,
                percentVarOpt: null,
                exposure: null,
                exposureOpt: null,
                euler: null,
                eulerOpt: null,
                zscore: null,
                _stripeIndex: stripeIndex++,
                isPlaceholder: true,
            })));
        });
    } else {
        RISK_OVERVIEW_FACTOR_GROUP_ORDER.forEach((groupName) => {
            const groupFactors = sortedGroups[groupName];
            if (groupFactors.length === 0) return;
            rows.push(Object.freeze({
                id: `bucket-${groupName}`,
                type: 'bucket',
                bucketLabel: groupName,
                label: groupName,
                _stripeIndex: null,
            }));
            const groupNames = groupFactors.map((factor) => String(factor.factor || '').trim());
            const actualGroupFactors = groupNames.map((name) => actualByName.get(name)).filter(Boolean);
            const selectedGroupFactors = groupNames.map((name) => selectedByName.get(name)).filter(Boolean);
            const groupTotal = Object.freeze({
                id: `total-${groupName}`,
                type: 'category-total',
                bucketLabel: groupName,
                label: `${groupName} Total`,
                percentVar: sumFactorValues(actualGroupFactors, 'percentVar'),
                percentVarOpt: sumFactorValues(selectedGroupFactors, 'percentVar'),
                exposure: sumFactorValues(actualGroupFactors, 'exposure'),
                exposureOpt: sumFactorValues(selectedGroupFactors, 'exposure'),
                euler: sumFactorValues(actualGroupFactors, 'euler'),
                eulerOpt: sumFactorValues(selectedGroupFactors, 'euler'),
                zscore: null,
                _stripeIndex: null,
            });
            rows.push(groupTotal);
            const groupExportRows = groupFactors.map((factor) => {
                const factorName = String(factor.factor || '').trim();
                const actual = actualByName.get(factorName);
                const selected = selectedByName.get(factorName);
                const row = Object.freeze({
                    id: `${groupName}-${factorName}`,
                    type: 'factor',
                    bucketLabel: groupName,
                    factor: factorName,
                    label: factorName,
                    actualDetailAvailable: actualByName.has(factorName),
                    percentVar: factorValue(actual, 'percentVar'),
                    percentVarOpt: factorValue(selected, 'percentVar'),
                    exposure: factorValue(actual, 'exposure'),
                    exposureOpt: factorValue(selected, 'exposure'),
                    euler: factorValue(actual, 'euler'),
                    eulerOpt: factorValue(selected, 'euler'),
                    zscore: factorValue(actual ?? selected, 'zscore'),
                    _stripeIndex: stripeIndex++,
                });
                rows.push(row);
                return Object.freeze({
                    Factor: factorName || '-',
                    '% Var': formatPercentPoints(row.percentVar),
                    ...(includeSelectedExport ? {
                        '% Var OPT': formatPercentPoints(row.percentVarOpt),
                    } : {}),
                    Exposure: formatInteger(row.exposure),
                    ...(includeSelectedExport ? {
                        'Exposure OPT': formatInteger(row.exposureOpt),
                    } : {}),
                    'Factor Risk': formatInteger(row.euler),
                    ...(includeSelectedExport ? {
                        'Factor Risk OPT': formatInteger(row.eulerOpt),
                    } : {}),
                    'Rtn. Z-Score': row.zscore == null ? '-' : row.zscore.toFixed(2),
                });
            });
            groupedExportRows[groupName] = [Object.freeze({
                Factor: groupTotal.label,
                '% Var': formatPercentPoints(groupTotal.percentVar),
                ...(includeSelectedExport ? {
                    '% Var OPT': formatPercentPoints(groupTotal.percentVarOpt),
                } : {}),
                Exposure: formatInteger(groupTotal.exposure),
                ...(includeSelectedExport ? {
                    'Exposure OPT': formatInteger(groupTotal.exposureOpt),
                } : {}),
                'Factor Risk': formatInteger(groupTotal.euler),
                ...(includeSelectedExport ? {
                    'Factor Risk OPT': formatInteger(groupTotal.eulerOpt),
                } : {}),
                'Rtn. Z-Score': '-',
            }), ...groupExportRows];
        });
    }

    return Object.freeze({
        hasSelectedData,
        rows: Object.freeze(rows),
        summaryExportRows: Object.freeze(summaryExportRows),
        groupedExportRows: Object.freeze(groupedExportRows),
    });
}

export const formatGroupValue = (record, key) => {
    if (!key) return '-';
    const value = toNullableNumber(record?.[key]);
    if (value == null) return '-';
    if (key.includes('pct')) {
        const rounded = (value * 100).toFixed(2);
        return `${rounded === '-0.00' ? '0.00' : rounded}%`;
    }
    return (Math.round(value) || 0).toLocaleString();
};

// A missing group means no holdings only after every position was calculated.
// SUCCESS alone is insufficient: missing-price coverage can still be partial.
export function emptyAttributeGroupMetrics(payload, result) {
    const coverage = payload?.risk_calculation_coverage;
    const counts = ['total_positions', 'included_count', 'pending_count', 'excluded_count', 'failed_count'];
    const resourceState = payload?.riskOverviewResource?.state;
    if (
        payload?.contract !== 'risk-overview-attribute-groups.v1'
        || payload?.success !== true || payload?.status !== 'SUCCESS'
        || result?.success !== true || result?.status !== 'SUCCESS'
        || !Array.isArray(result?.groups)
        || (resourceState != null && resourceState !== 'ready')
        || !counts.every((key) => Number.isInteger(coverage?.[key]) && coverage[key] >= 0)
        || coverage.included_count !== coverage.total_positions
        || coverage.pending_count !== 0 || coverage.excluded_count !== 0 || coverage.failed_count !== 0
    ) return null;

    const aum = toNullableNumber(payload?.portfolio_risks?.aum);
    return {
        nmv: 0, beta_exp: 0, factor_vol: 0, idio_vol: 0, total_vol: 0, gmv: 0, ucap: 0,
        aum,
        nmv_pct_aum: aum > 0 ? 0 : null,
        beta_exp_pct_aum: aum > 0 ? 0 : null,
        factor_vol_pct_aum: aum > 0 ? 0 : null,
        idio_vol_pct_aum: aum > 0 ? 0 : null,
        total_vol_pct_aum: aum > 0 ? 0 : null,
        // UCap and total risk are zero, so these ratios are undefined.
        nmv_pct_ucap: null, beta_exp_pct_ucap: null, factor_vol_pct_ucap: null,
        idio_vol_pct_ucap: null, total_vol_pct_ucap: null, factor_pct: null, idio_pct: null,
    };
}

export function buildGroupPresentation(
    actualRows = [],
    selectedRows = [],
    { includeSelectedExport, actualEmptyGroupMetrics = null, selectedEmptyGroupMetrics = null } = {},
) {
    requireSelectedExportDecision(includeSelectedExport);
    const actualByGroup = new Map(
        (Array.isArray(actualRows) ? actualRows : [])
            .filter((row) => String(row?.category || '').trim())
            .map((row) => [String(row.category).trim(), row]),
    );
    const selectedByGroup = new Map(
        (Array.isArray(selectedRows) ? selectedRows : [])
            .filter((row) => String(row?.category || '').trim())
            .map((row) => [String(row.category).trim(), row]),
    );
    const groupNames = [...new Set([
        ...actualByGroup.keys(),
        ...selectedByGroup.keys(),
    ])].sort((left, right) => left.localeCompare(right));
    const exportGroupNames = includeSelectedExport
        ? groupNames
        : [...actualByGroup.keys()].sort((left, right) => left.localeCompare(right));
    groupNames.forEach((group) => {
        if (!actualByGroup.has(group) && actualEmptyGroupMetrics) {
            actualByGroup.set(group, { category: group, ...actualEmptyGroupMetrics });
        }
        if (!selectedByGroup.has(group) && selectedEmptyGroupMetrics) {
            selectedByGroup.set(group, { category: group, ...selectedEmptyGroupMetrics });
        }
    });
    const exportRows = exportGroupNames.flatMap((group) => {
        const actual = actualByGroup.get(group);
        const selected = selectedByGroup.get(group);
        return GROUP_METRICS.map(({ label, absKey, pctUcapKey, pctAumKey }) => Object.freeze({
            Group: group,
            Metric: label,
            'Exp.': formatGroupValue(actual, absKey),
            ...(includeSelectedExport ? {
                'Exp. OPT': formatGroupValue(selected, absKey),
            } : {}),
            '% UCap': formatGroupValue(actual, pctUcapKey),
            ...(includeSelectedExport ? {
                '% UCap OPT': formatGroupValue(selected, pctUcapKey),
            } : {}),
            '% AUM': formatGroupValue(actual, pctAumKey),
            ...(includeSelectedExport ? {
                '% AUM OPT': formatGroupValue(selected, pctAumKey),
            } : {}),
        }));
    });
    return Object.freeze({
        actualByGroup,
        selectedByGroup,
        groupNames: Object.freeze(groupNames),
        exportRows: Object.freeze(exportRows),
    });
}

export function buildAttributeRiskExportRows(
    actualMetrics = {},
    selectedMetrics = {},
    { includeSelectedExport } = {},
) {
    requireSelectedExportDecision(includeSelectedExport);
    const selectedMatches = includeSelectedExport
        && selectedMetrics?.contract === 'risk-overview-attribute-groups.v1'
        && [
            'watchlist_revision_id',
            'attribute_definition_fingerprint',
            'attribute_content_fingerprint',
        ].every((field) => (
            Boolean(actualMetrics?.[field])
            && selectedMetrics?.[field] === actualMetrics[field]
        ));
    const actualResults = new Map(
        (Array.isArray(actualMetrics?.attribute_results)
            ? actualMetrics.attribute_results
            : []).map((result) => [String(result.attribute_definition_id), result]),
    );
    const selectedResults = new Map(
        (selectedMatches && Array.isArray(selectedMetrics?.attribute_results)
            ? selectedMetrics.attribute_results
            : []).map((result) => [String(result.attribute_definition_id), result]),
    );
    return (Array.isArray(actualMetrics?.attribute_definitions)
        ? actualMetrics.attribute_definitions
        : []).filter(isWatchlistGroupingAttribute).flatMap((definition) => {
        const definitionId = String(definition.attribute_definition_id);
        const actualResult = actualResults.get(definitionId);
        const selectedResult = selectedResults.get(definitionId);
        const actualGroups = actualResult?.groups ?? [];
        const selectedGroups = selectedResult?.groups ?? [];
        const toGroupRows = (groups) => (Array.isArray(groups) ? groups : [])
            .filter((group) => isAssignedAttributeGroup(group?.group))
            .map((group) => ({
                category: String(group?.display_label || '').trim(),
                ...(group?.metrics ?? {}),
            })).filter((row) => row.category);
        const presentation = buildGroupPresentation(
            toGroupRows(actualGroups),
            toGroupRows(selectedGroups),
            {
                includeSelectedExport: selectedMatches,
                actualEmptyGroupMetrics: selectedMatches
                    ? emptyAttributeGroupMetrics(actualMetrics, actualResult) : null,
                selectedEmptyGroupMetrics: selectedMatches
                    ? emptyAttributeGroupMetrics(selectedMetrics, selectedResult) : null,
            },
        );
        return presentation.exportRows.map(({ Group, ...row }) => Object.freeze({
            Attribute: String(definition.display_name || ''),
            Category: Group,
            Assignment: 'Assigned',
            ...row,
        }));
    });
}
